import React, { useMemo, useRef, useState } from 'react';

import { GoogleMap, InfoWindow, Marker, useLoadScript } from '@react-google-maps/api';
import { Radio, Skeleton } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { heatCells, heatCellColor } from './heatGrid';
import { useHeatGridLayer } from './heatGridLayer';
import { issueLabel, sortedIssues, stationTitle, worstSeverity } from './stationIssues';


import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../../../constants/googleMaps';
import { IntegrationStation } from '../../../../types/responses/Interoperability';
import { MapErrorBoundary } from '../../../molecules/MapErrorBoundary';
import { directionLabel } from '../contractRules';
import { FilterSpacer, ViewSwitch } from '../styled';

const Frame = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  overflow: hidden;

  .map-container {
    width: 100%;
    height: 520px;
  }
`;

/** Barra acima do mapa: alternador de camada e, no calor, onde estao as concentracoes. */
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 12px;
  border-bottom: 1px solid #e9ebee;
  background: #fafbfc;
`;

const HeatError = styled.span`
  font-size: 11.5px;
  color: #b02525;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const Densest = styled.span`
  font-size: 11.5px;
  color: #4a5462;
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 12.5px;
`;

const Legend = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  padding: 8px 14px;
  border-top: 1px solid #e9ebee;
  background: #fafbfc;
  font-size: 11.5px;
  color: #6b7280;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Bullet = styled.span<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

/** A escala da grade, com as mesmas cores que o mapa desenha. */
const Scale = styled.span`
  display: inline-flex;
  gap: 2px;
`;

const ScaleStep = styled.span<{ color: string }>`
  width: 22px;
  height: 8px;
  background: ${({ color }) => color};
`;

const InfoBox = styled.div`
  font-size: 12px;
  color: #2d3339;
  max-width: 220px;

  strong {
    display: block;
    margin-bottom: 2px;
  }

  p {
    margin: 2px 0 0;
    color: #6b7280;
  }
`;

const OpenLink = styled.button`
  margin-top: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #1e4f9c;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
`;

/**
 * google.maps.SymbolPath.CIRCLE.
 *
 * O valor literal, e não o global `google`: o objeto do ícone é montado no render do
 * componente, que roda antes de o script do Maps existir — e no teste ele não existe
 * nunca.
 */
const CIRCLE = 0;

/** Florianópolis, o mesmo padrão do mapa da home quando não há de onde partir. */
const DEFAULT_CENTER = { lat: -27.5713144, lng: -48.75047 };

/**
 * Cor do marcador pela saúde do cadastro.
 *
 * Verde, laranja e vermelho, o mesmo código do resto da aba. O sentido do contrato não
 * entra na cor de propósito: ele já é filtro e já está no detalhe, e duas dimensões na
 * mesma cor não se leem de relance num mapa com centenas de pontos.
 */
const MARKER_COLOR = {
  OK: '#26a35c',
  ATTENTION: '#e0700f',
  BLOCKING: '#c8352f',
};

const colorOf = (station: IntegrationStation): string => {
  const severity = worstSeverity(station.issues || []);

  if (!severity) {
    return MARKER_COLOR.OK;
  }

  return severity === 'BLOCKING' ? MARKER_COLOR.BLOCKING : MARKER_COLOR.ATTENTION;
};

export type StationsMapProps = {
  /** Só as que têm coordenada; quem chama decide o que fazer com as outras. */
  stations: IntegrationStation[];

  onOpen: (station: IntegrationStation) => void;
};

/**
 * As estações da integração no mapa (MV2-1706).
 *
 * A lista responde "quais são"; o mapa responde "onde estão" — e mostra de um jeito que
 * a lista não consegue que uma região inteira do contrato está sem coordenada, ou que o
 * que o parceiro recebe está todo num canto só.
 *
 * Usa o @react-google-maps/api que o dashboard já usa na home, e não uma biblioteca
 * nova.
 */
export const StationsMap: React.FC<StationsMapProps> = ({ stations, onOpen }) => {
  // Configuracao unica do script, compartilhada com os outros mapas do dashboard: dois
  // carregadores com opcoes diferentes injetam dois scripts do Maps, e a pagina quebra.
  const { isLoaded, loadError } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);

  const [opened, setOpened] = useState<IntegrationStation | null>(null);
  const [layer, setLayer] = useState<'pins' | 'heat'>('pins');


  const points = useMemo(
    () =>
      stations
        .filter((station) => station.latitude !== null && station.longitude !== null)
        .map((station) => ({
          station,
          position: { lat: Number(station.latitude), lng: Number(station.longitude) },
        })),
    [stations],
  );

  const baseZoom = points.length === 1 ? 14 : 6;

  /**
   * O zoom corrente, só para o raio do calor acompanhar.
   *
   * Vem do mapa, e não do prop: o prop é o zoom inicial, e quem dá zoom depois é a pessoa.
   */
  const [zoom, setZoom] = useState(baseZoom);

  /**
   * O mapa em estado, e nao so em ref: a camada de calor e criada num efeito, e efeito nao
   * roda de novo quando uma ref muda.
   */
  const [map, setMap] = useState<any>(null);
  const mapRef = useRef<any>(null);

  /**
   * A densidade em células, para o zoom corrente.
   *
   * Não usa Heatmap Layer porque ele não existe mais: o Google o removeu da Maps
   * JavaScript API na versão 3.65, e o @react-google-maps/api pede v=weekly. A mensagem
   * que aparecia era literalmente "no longer available as of version 3.65".
   *
   * Sem LatLng e sem biblioteca opcional, a camada deixou de depender de qual mapa do
   * dashboard carregou o script primeiro.
   */
  const cells = useMemo(
    () => heatCells(points.map((point) => point.position), zoom),
    [points, zoom],
  );

  /**
   * Onde estão as maiores concentrações, em número.
   *
   * O borrão de calor diz "aqui tem mais", e não quanto: as três primeiras cidades em
   * texto respondem a pergunta que o calor só insinua.
   *
   * Estação com coordenada e sem endereço fica fora do ranking, e é contada à parte. Ela
   * está no mapa, mas não se sabe em que cidade: juntá-las num item "sem endereço"
   * anunciaria uma concentração que não existe — no banco de dev essas 21 estão
   * espalhadas pelas seis cidades.
   */
  const densest = useMemo(() => {
    const byCity = new Map<string, number>();

    points.forEach(({ station }) => {
      if (!station.city) {
        return;
      }

      byCity.set(station.city, (byCity.get(station.city) || 0) + 1);
    });

    return Array.from(byCity.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [points]);

  /** Quantas estão no mapa sem dizer em que cidade. */
  const withoutCity = useMemo(
    () => points.filter(({ station }) => !station.city).length,
    [points],
  );

  /**
   * O centro sai da média dos pontos, e não de um fitBounds.
   *
   * fitBounds com um ponto só deixa o zoom no máximo, e a estação aparece sozinha no
   * meio de um quintal — a pessoa perde a referência de onde aquilo fica.
   */
  const center = useMemo(() => {
    if (points.length === 0) {
      return DEFAULT_CENTER;
    }

    const total = points.reduce(
      (sum, point) => ({ lat: sum.lat + point.position.lat, lng: sum.lng + point.position.lng }),
      { lat: 0, lng: 0 },
    );

    return { lat: total.lat / points.length, lng: total.lng / points.length };
  }, [points]);

  const { error: heatError } = useHeatGridLayer(layer === 'heat' ? map : null, cells);

  if (loadError) {
    return (
      <Frame>
        <Empty>{T('Could not load the map.')}</Empty>
      </Frame>
    );
  }

  if (!isLoaded) {
    return (
      <Frame>
        <Empty>
          <Skeleton active />
        </Empty>
      </Frame>
    );
  }

  return (
    <Frame>
      {points.length > 0 && (
        <Bar>
          {/* O que o Google respondeu, em vez de um mapa que ignora o botao em silencio. */}
          {layer === 'heat' && heatError && (
            <HeatError>
              {T('The density layer failed:')} {heatError}
            </HeatError>
          )}

          {layer === 'heat' && densest.length > 0 && (
            <Densest>
              {T('Most stations in')}{' '}
              {densest.map(([city, count]) => `${city} (${count})`).join(' · ')}
              {withoutCity > 0 && (
                <>
                  {' · '}
                  {withoutCity} {T('on the map without an address')}
                </>
              )}
            </Densest>
          )}

          <FilterSpacer />

          {/*
            Duas perguntas diferentes sobre o mesmo conjunto: "qual estacao e essa" se
            responde com pino, "onde tem mais" com densidade. Em densidade os pinos saem de cena
            de proposito — 148 circulos coloridos por cima do borrao escondem justamente
            a densidade que o calor existe para mostrar.
          */}
          <ViewSwitch
            value={layer}
            size="small"
            buttonStyle="solid"
            onChange={(event) => setLayer(event.target.value)}
          >
            <Radio.Button value="pins">{T('Points')}</Radio.Button>
            <Radio.Button value="heat">{T('Density')}</Radio.Button>
          </ViewSwitch>
        </Bar>
      )}

      {points.length === 0 && (
        <Empty>{T('No station in this filter has coordinates to show on the map.')}</Empty>
      )}

      {points.length > 0 && (
        <MapErrorBoundary>
        <GoogleMap
          mapContainerClassName="map-container"
          zoom={baseZoom}
          center={center}
          onLoad={(loaded) => {
            mapRef.current = loaded;
            setMap(loaded);
          }}
          onZoomChanged={() => {
            const current = mapRef.current?.getZoom();

            if (typeof current === 'number') {
              setZoom(current);
            }
          }}
        >
          {layer === 'pins' && points.map(({ station, position }) => (
            <Marker
              key={station.charge_box_id}
              position={position}
              title={stationTitle(station)}
              icon={{
                path: CIRCLE,
                scale: 7,
                fillColor: colorOf(station),
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
              }}
              onClick={() => setOpened(station)}
            >
              {opened?.charge_box_id === station.charge_box_id && (
                <InfoWindow position={position} onCloseClick={() => setOpened(null)}>
                  <InfoBox>
                    <strong>{stationTitle(station)}</strong>
                    <p>{directionLabel(station.direction)}</p>
                    <p>
                      {station.partners && station.partners.length > 0
                        ? station.partners.map((partner) => partner.name).join(', ')
                        : T('none')}
                    </p>

                    {sortedIssues(station.issues || []).length > 0 && (
                      <p>
                        {sortedIssues(station.issues || [])
                          .map((issue) => issueLabel(issue))
                          .join(' · ')}
                      </p>
                    )}

                    {/* Mesmo painel da lista: o mapa nao ganha um detalhe proprio. */}
                    <OpenLink type="button" onClick={() => onOpen(station)}>
                      {T('Open details')}
                    </OpenLink>
                  </InfoBox>
                </InfoWindow>
              )}
            </Marker>
          ))}
        </GoogleMap>
        </MapErrorBoundary>
      )}

      {/* Legenda so quando ha o que legendar: com zero pinos ela e ruido. */}
      {points.length > 0 && (
      <Legend>
        {layer === 'pins' && (
          <>
            <LegendItem>
              <Bullet color={MARKER_COLOR.OK} />
              {T('Correct')}
            </LegendItem>
            <LegendItem>
              <Bullet color={MARKER_COLOR.ATTENTION} />
              {T('Usable, with wrong data')}
            </LegendItem>
            <LegendItem>
              <Bullet color={MARKER_COLOR.BLOCKING} />
              {T('The partner cannot use it')}
            </LegendItem>
          </>
        )}

        {/* No calor a legenda das cores nao vale: a cor passa a ser quantidade. */}
        {layer === 'heat' && (
          <LegendItem>
            {T('Fewer stations')}
            <Scale>
              {[0.15, 0.4, 0.65, 0.95].map((intensity) => (
                <ScaleStep key={intensity} color={heatCellColor(intensity)} />
              ))}
            </Scale>
            {T('More stations')}
          </LegendItem>
        )}
      </Legend>
      )}
    </Frame>
  );
};
